<template>
  <section class="advanced-settings-page">
    <div class="settings-container">
      <header class="page-header">
        <div>
          <p class="eyebrow">Configuration</p>
          <h1>Advanced Settings</h1>
          <p class="description">
            Fine-tune how your AI agent responds, balances creativity, and handles response variation.
          </p>
        </div>

        <div class="status-badge">
          Expert mode
        </div>
      </header>

      <div class="info-box">
        <strong>Recommended for advanced users.</strong>
        These settings affect the behavior and consistency of your AI agent. For most business use cases,
        the default values are already optimized.
      </div>

      <form @submit.prevent="saveSettings" class="settings-form">
        <div class="setting-card">
          <div class="setting-header">
            <div>
              <label for="temperature">Temperature</label>
              <p>
                Controls creativity. Lower values make answers more predictable. Higher values make them more varied.
              </p>
            </div>

            <span class="value-pill">{{ settings.temperature }}</span>
          </div>

          <input
            type="range"
            id="temperature"
            v-model.number="settings.temperature"
            min="0"
            max="1"
            step="0.01"
          />

          <div class="range-labels">
            <span>Precise</span>
            <span>Creative</span>
          </div>
        </div>

        <div class="setting-card">
          <div class="setting-header">
            <div>
              <label for="topP">Top P</label>
              <p>
                Controls response diversity by limiting the probability pool used by the model.
              </p>
            </div>

            <span class="value-pill">{{ settings.top_p }}</span>
          </div>

          <input
            type="range"
            id="topP"
            v-model.number="settings.top_p"
            min="0"
            max="1"
            step="0.01"
          />

          <div class="range-labels">
            <span>Focused</span>
            <span>Diverse</span>
          </div>
        </div>

        <div class="setting-card">
          <div class="setting-header">
            <div>
              <label for="frequencyPenalty">Frequency Penalty</label>
              <p>
                Reduces repeated wording. Increase this if the agent repeats the same phrases too often.
              </p>
            </div>

            <span class="value-pill">{{ settings.frequency_penalty }}</span>
          </div>

          <input
            type="range"
            id="frequencyPenalty"
            v-model.number="settings.frequency_penalty"
            min="0"
            max="2"
            step="0.1"
          />

          <div class="range-labels">
            <span>Neutral</span>
            <span>Less repetition</span>
          </div>
        </div>

        <div class="setting-card">
          <div class="setting-header">
            <div>
              <label for="presencePenalty">Presence Penalty</label>
              <p>
                Encourages the agent to introduce new ideas instead of staying too close to previous wording.
              </p>
            </div>

            <span class="value-pill">{{ settings.presence_penalty }}</span>
          </div>

          <input
            type="range"
            id="presencePenalty"
            v-model.number="settings.presence_penalty"
            min="0"
            max="2"
            step="0.1"
          />

          <div class="range-labels">
            <span>Stable</span>
            <span>Exploratory</span>
          </div>
        </div>

        <div class="advanced-grid">
          <div class="toggle-card">
            <div>
              <label for="safeMode">Business Safe Mode</label>
              <p>
                Keeps answers professional, conservative, and aligned with business workflows.
              </p>
            </div>

            <input
              id="safeMode"
              type="checkbox"
              v-model="settings.business_safe_mode"
            />
          </div>

          <div class="toggle-card">
            <div>
              <label for="askOneQuestion">Ask One Question at a Time</label>
              <p>
                Prevents the agent from overwhelming callers with too many questions at once.
              </p>
            </div>

            <input
              id="askOneQuestion"
              type="checkbox"
              v-model="settings.ask_one_question_at_a_time"
            />
          </div>
        </div>

        <div class="form-actions">
          <button type="button" class="secondary-button" @click="resetDefaults">
            Reset defaults
          </button>

          <button type="submit" :disabled="saving">
            {{ saving ? 'Saving...' : 'Save settings' }}
          </button>
        </div>

        <p v-if="successMessage" class="success-message">
          {{ successMessage }}
        </p>

        <p v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </p>
      </form>
    </div>
  </section>
</template>

<script>
import Cookies from 'js-cookie'

export default {
  name: 'AdvancedSettings',

  data() {
    return {
      saving: false,
      successMessage: '',
      errorMessage: '',

      settings: {
        temperature: 0.4,
        top_p: 0.9,
        frequency_penalty: 0,
        presence_penalty: 0,
        business_safe_mode: true,
        ask_one_question_at_a_time: true
      }
    }
  },

  mounted() {
    const token = Cookies.get('token')

    if (!token) {
      this.$router.push('/login')
    }
  },

  methods: {
    async saveSettings() {
      this.saving = true
      this.successMessage = ''
      this.errorMessage = ''

      try {
        // Later: replace with your API call.
        // Example:
        // await api.advancedSettings.update(this.settings)

        await new Promise((resolve) => setTimeout(resolve, 500))

        this.successMessage = 'Advanced settings saved successfully.'
      } catch (error) {
        console.error(error)
        this.errorMessage = 'Unable to save advanced settings. Please try again.'
      } finally {
        this.saving = false
      }
    },

    resetDefaults() {
      this.settings = {
        temperature: 0.4,
        top_p: 0.9,
        frequency_penalty: 0,
        presence_penalty: 0,
        business_safe_mode: true,
        ask_one_question_at_a_time: true
      }

      this.successMessage = ''
      this.errorMessage = ''
    }
  }
}
</script>

<style scoped>
.advanced-settings-page {
  min-height: 100vh;
  background: #f8fafc;
  padding: 48px 20px;
  color: #0f172a;
}

.settings-container {
  max-width: 880px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-start;
  margin-bottom: 24px;
}

.eyebrow {
  color: #6366f1;
  font-size: 0.78rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  margin-bottom: 10px;
}

h1 {
  font-size: 2.5rem;
  font-weight: 900;
  letter-spacing: -0.05em;
  color: #020617;
  margin-bottom: 10px;
}

.description {
  max-width: 680px;
  color: #64748b;
  line-height: 1.7;
}

.status-badge {
  background: #020617;
  color: white;
  border-radius: 999px;
  padding: 10px 16px;
  font-size: 0.82rem;
  font-weight: 900;
  white-space: nowrap;
}

.info-box {
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  color: #3730a3;
  border-radius: 18px;
  padding: 16px 18px;
  line-height: 1.6;
  margin-bottom: 24px;
  font-size: 0.94rem;
}

.settings-form {
  display: grid;
  gap: 18px;
}

.setting-card,
.toggle-card {
  background: white;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 18px 60px rgba(15, 23, 42, 0.05);
}

.setting-header,
.toggle-card {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-start;
}

.setting-header label,
.toggle-card label {
  display: block;
  font-size: 1.02rem;
  font-weight: 900;
  color: #020617;
  margin-bottom: 8px;
}

.setting-header p,
.toggle-card p {
  color: #64748b;
  line-height: 1.6;
  font-size: 0.92rem;
  margin: 0;
}

.value-pill {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #020617;
  border-radius: 999px;
  padding: 8px 12px;
  font-size: 0.82rem;
  font-weight: 900;
  min-width: 58px;
  text-align: center;
}

input[type='range'] {
  width: 100%;
  margin-top: 22px;
  accent-color: #020617;
}

.range-labels {
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  color: #94a3b8;
  font-size: 0.78rem;
  font-weight: 700;
}

.advanced-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.toggle-card {
  align-items: center;
}

.toggle-card input[type='checkbox'] {
  width: 46px;
  height: 26px;
  accent-color: #020617;
  cursor: pointer;
  flex-shrink: 0;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}

.form-actions button {
  border: none;
  border-radius: 16px;
  padding: 14px 20px;
  font-weight: 900;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease, background 0.2s ease;
}

.form-actions button[type='submit'] {
  background: #020617;
  color: white;
}

.form-actions button[type='submit']:hover {
  transform: scale(1.02);
  background: #111827;
}

.form-actions button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}

.secondary-button {
  background: white;
  color: #020617;
  border: 1px solid rgba(15, 23, 42, 0.12) !important;
}

.secondary-button:hover {
  background: #f1f5f9;
  transform: scale(1.02);
}

.success-message,
.error-message {
  border-radius: 16px;
  padding: 14px 16px;
  font-weight: 700;
  font-size: 0.92rem;
}

.success-message {
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
  color: #166534;
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

@media (max-width: 760px) {
  .advanced-settings-page {
    padding: 28px 14px;
  }

  .page-header,
  .setting-header,
  .toggle-card,
  .form-actions {
    flex-direction: column;
  }

  .advanced-grid {
    grid-template-columns: 1fr;
  }

  h1 {
    font-size: 2rem;
  }

  .status-badge {
    width: fit-content;
  }

  .form-actions button {
    width: 100%;
  }
}
</style>